/**
 * The fidelity trace: one line of JSON per simulation tick, in the format the instrumented
 * original writes.
 *
 * The point is a tick-by-tick diff against a recording made by the 2013 game itself, so
 * everything here is dictated by what that game emits rather than by what would be convenient
 * on this side. The format is written down in `docs/fidelity-harness.md`; the producer it has
 * to agree with is `tools/fidelity/original-trace.patch`.
 *
 * FOUR THINGS THAT LOOK LIKE DETAILS AND ARE NOT:
 *
 *  1. WHERE THE SAMPLE IS TAKEN. The original hooks the very end of `Match::Process()`, after
 *     the referee, the ball, all twenty-two players, the collisions and the clocks, and
 *     immediately before its iteration counter increments. Sampling one step earlier or later
 *     produces a trace that is perfectly well formed and compares wrongly, with no symptom
 *     anywhere. So this writer is not called by the game loop at all: `Match.finishStep` calls
 *     it as its own last act, which is the same boundary, and no caller can move it.
 *
 *  2. THE BALL POSITION IS THE FORECAST'S ZERO SAMPLE, not the live ball. Asking the ball
 *     where it is gives its position at the START of the tick, because the forecast is built
 *     before the step and the step is adopted out of it. The original traces exactly that, and
 *     it is visible in a real recording: on its first tick the ball still reads 0.11 m up
 *     while its velocity already carries one tick of gravity.
 *
 *  3. SIX DECIMALS, ALWAYS. The original writes through a C++ stream fixed at six decimal
 *     places, so 0 is `0.000000` and never `0`. A non-finite number is not representable in
 *     JSON and must fail the run rather than be written as `NaN`.
 *
 *  4. TWENTY-TWO PLAYERS, ALWAYS, IN ROSTER ORDER: team 0 slots 0 to 10, then team 1 slots 0
 *     to 10. A sent-off player keeps his slot and is marked inactive. Fixed cardinality is
 *     what stops an array shift from reading as a physics difference.
 */

import { Vector3 } from 'three';
import type { Match, MatchPlayer } from './match';
import { MatchPhase } from './referee';

export const TRACE_FORMAT = 'gameplay-football-trace';
export const TRACE_FORMAT_VERSION = 1;
export const TRACE_TICK_HZ = 100;
export const TRACE_SAMPLING = 'post_process_before_iteration_increment';
export const TRACE_COORDINATE_SYSTEM = 'original_world_xyz';

/**
 * How many ticks a recording keeps by default: 57.14 seconds.
 *
 * Not an arbitrary round number. Two runs of the ORIGINAL with the same seed and the same
 * scripted input are byte-identical up to tick 5714 and then diverge at a ball contact,
 * confirmed three times at exactly the same tick. Past that point a difference between the
 * two implementations cannot be attributed to this one without checking a second original
 * run first, so a recording that stops there is the longest one that means anything.
 */
export const TRACE_DEFAULT_MAX_TICKS = 5715;

/** `e_MatchPhase`, `gamedefines.hpp:129-136`. */
const PHASE_NUMBER: Record<MatchPhase, number> = {
  [MatchPhase.PreMatch]: 0,
  [MatchPhase.FirstHalf]: 1,
  [MatchPhase.SecondHalf]: 2,
  [MatchPhase.FirstExtraTime]: 3,
  [MatchPhase.SecondExtraTime]: 4,
  [MatchPhase.Penalties]: 5,
};

/**
 * `e_FunctionType`, `gamedefines.hpp:93-108`, keyed the way the original's own string lookup
 * keys it (`StringToFunctionType`, gamedefines.cpp:112-127).
 *
 * Note what is missing: the original's lookup has no entry for a header, so the value 7 can
 * never be produced from a command and anything unrecognised comes out as 0. Reproduced
 * rather than corrected — a trace has to speak the original's numbering, including its gaps.
 */
const FUNCTION_NUMBER: Readonly<Record<string, number>> = {
  movement: 1,
  ballcontrol: 2,
  trap: 3,
  shortpass: 4,
  longpass: 5,
  highpass: 6,
  shot: 8,
  deflect: 9,
  catch: 10,
  interfere: 11,
  trip: 12,
  sliding: 13,
  special: 14,
};

export interface TraceRun {
  /** Identifies the attempted reproduction. Required. */
  id: string;
  /** Identifies the immutable input recording both runs replayed. Null when there is none. */
  inputId?: string | null;
  /** The seed this run actually consumed. Null means the run is not a comparison candidate. */
  rngSeed?: number | null;
  /** Build revision. Null only while bootstrapping. */
  sourceRevision?: string | null;
}

export interface TraceTeam {
  /** The team's row in the shipped database, so the two traces can be checked for pairing. */
  teamDbId: number;
  name: string;
}

/** One roster place. The player is followed by reference; nothing is copied up front. */
export interface TraceRosterEntry {
  team: 0 | 1;
  /** 0 to 10, the starting-XI place. Not a changing player-selection id. */
  slot: number;
  playerDbId: number;
  player: MatchPlayer;
}

export interface TraceOptions {
  run: TraceRun;
  /** Side 0 first, then side 1. */
  teams: [TraceTeam, TraceTeam];
  /** Exactly 22, team 0 slots 0-10 then team 1 slots 0-10. */
  roster: TraceRosterEntry[];
  /** Stop after this many ticks. 0 records without limit. */
  maxTicks?: number;
  /** Called once, when the recording reaches its tick limit. Not on a stop by hand. */
  onComplete?: (trace: FidelityTrace) => void;
}

/**
 * Format one number the way the original's stream does: fixed point, six decimals.
 *
 * Six decimals is one micrometre, deliberately finer than any useful comparison tolerance, so
 * that the text carries no rounding decisions of its own.
 */
export function traceNumber(value: number): string {
  if (!Number.isFinite(value)) throw new RangeError(`non-finite value in trace: ${value}`);
  // C++ prints a negative zero with its sign; JavaScript drops it. Nothing reads the sign of
  // a zero, but the two files should be diffable as text.
  if (Object.is(value, -0)) return '-0.000000';
  return value.toFixed(6);
}

function vector(value: Vector3): string {
  return `[${traceNumber(value.x)},${traceNumber(value.y)},${traceNumber(value.z)}]`;
}

/** JSON's own string escaping, which is what the original's escaper reproduces by hand. */
function text(value: string): string {
  return JSON.stringify(value);
}

function orNull(value: string | null | undefined): string {
  return value === null || value === undefined ? 'null' : text(value);
}

/**
 * Say a clip's name the way the original says it.
 *
 * The original names a clip by the file it was loaded from — `media/animations/…/000.anim` —
 * and appends `_mirror` to the copy it makes of every clip by reflecting it. Our asset
 * converter names the same clip by its path stem and marks the mirror with a trailing `|m`.
 * Same clip, two spellings, and a comparison that did not reconcile them would report all
 * twenty-two players as playing the wrong animation on every single tick.
 *
 * The procedurally generated clips cannot be reconciled: both sides invent names for them and
 * the two schemes describe different things. They keep ours, and a comparison has to treat a
 * name beginning `autogen/` as incomparable.
 */
export function originalClipName(name: string): string {
  const mirrored = name.endsWith('|m');
  const stem = mirrored ? name.slice(0, -2) : name;
  if (stem.startsWith('autogen/')) return mirrored ? `${stem}_mirror` : stem;
  return `media/animations/${stem}.anim${mirrored ? '_mirror' : ''}`;
}

/**
 * A recording in progress.
 *
 * Lines are held in memory rather than streamed, because a browser has nowhere to stream them
 * to. At roughly 5 kB a tick the default cap is about 28 MB, which is large but not a problem;
 * an uncapped recording of a whole match is not, and would eventually exhaust the tab.
 */
export class FidelityTrace {
  private readonly lines: string[] = [];
  private readonly roster: TraceRosterEntry[];
  private readonly maxTicks: number;
  private readonly onComplete: ((trace: FidelityTrace) => void) | undefined;
  private readonly ballPosition = new Vector3();
  private readonly ballVelocity = new Vector3();

  private ticks = 0;
  private stopped = false;
  /** Set when a tick could not be written. A trace that stopped early says why. */
  failure: string | null = null;

  constructor(options: TraceOptions) {
    this.roster = options.roster;
    this.maxTicks = options.maxTicks ?? TRACE_DEFAULT_MAX_TICKS;
    this.onComplete = options.onComplete;

    if (this.roster.length !== 22) {
      throw new Error(`a trace needs exactly 22 roster places, got ${this.roster.length}`);
    }
    this.roster.forEach((entry, index) => {
      const team = index < 11 ? 0 : 1;
      const slot = index % 11;
      if (entry.team !== team || entry.slot !== slot) {
        throw new Error(
          `roster place ${index} must be team ${team} slot ${slot}, got team ${entry.team} slot ${entry.slot}`,
        );
      }
    });

    this.lines.push(this.header(options));
  }

  private header(options: TraceOptions): string {
    const { run, teams } = options;
    const side = (index: 0 | 1): string =>
      `{"side":${index},"team_db_id":${teams[index].teamDbId},"name":${text(teams[index].name)}}`;
    return (
      `{"kind":"header","format":${text(TRACE_FORMAT)},` +
      `"format_version":${TRACE_FORMAT_VERSION},"tick_hz":${TRACE_TICK_HZ},` +
      `"sampling":${text(TRACE_SAMPLING)},"coordinate_system":${text(TRACE_COORDINATE_SYSTEM)},` +
      `"run":{"id":${text(run.id)},"input_id":${orNull(run.inputId)},` +
      `"rng_seed":${run.rngSeed === null || run.rngSeed === undefined ? 'null' : run.rngSeed >>> 0},` +
      `"source_revision":${orNull(run.sourceRevision)}},` +
      `"teams":[${side(0)},${side(1)}]}`
    );
  }

  /** How many tick lines have been recorded. */
  get tickCount(): number {
    return this.ticks;
  }

  get recording(): boolean {
    return !this.stopped;
  }

  /**
   * Record one tick.
   *
   * Called by `Match.finishStep` and by nothing else — see note 1 at the top of this file.
   */
  writeTick(match: Match): void {
    if (this.stopped) return;

    let line: string;
    try {
      line = this.tickLine(match);
    } catch (error) {
      // The original prints a diagnostic and closes the file rather than writing something
      // that is not JSON. Same here: a short valid trace beats a long unparseable one.
      this.failure = error instanceof Error ? error.message : String(error);
      console.error(`[gf] fidelity trace stopped at tick ${this.ticks}: ${this.failure}`);
      this.stop();
      return;
    }

    this.lines.push(line);
    this.ticks++;
    if (this.maxTicks > 0 && this.ticks >= this.maxTicks) {
      // Only a recording that ran its full length is handed over. A recording stopped by hand
      // or by a bad number is left where it is, for whoever stopped it to read.
      this.stop();
      this.onComplete?.(this);
    }
  }

  private tickLine(match: Match): string {
    match.ball.predict(0, this.ballPosition);
    match.ball.movement(this.ballVelocity);

    let players = '';
    for (let index = 0; index < this.roster.length; index++) {
      const entry = this.roster[index]!;
      const player = entry.player;
      const anim = player.anim;
      players +=
        `${index ? ',' : ''}{"team":${entry.team},"slot":${entry.slot},` +
        `"player_db_id":${entry.playerDbId},"active":${player.active},` +
        `"position":${vector(player.position)},"velocity":${vector(player.movement)},` +
        `"clip":${text(originalClipName(anim.clip.name))},"clip_frame":${anim.frameNum},` +
        `"function":${FUNCTION_NUMBER[anim.currentFunctionType] ?? 0}}`;
    }

    return (
      `{"kind":"tick","tick":${this.ticks},"actual_time_ms":${match.timeMs},` +
      // The original holds its football clock in an integer and ours in a float, because ours
      // does not want the truncation the original takes on every single tick. Rounded down at
      // the point of writing, so the field carries the same units and the same meaning.
      `"match_time_ms":${Math.floor(match.matchTimeMs)},` +
      `"phase":${PHASE_NUMBER[match.phase] ?? 0},"in_play":${match.inPlay},` +
      `"in_set_piece":${match.inSetPiece},` +
      `"ball":{"position":${vector(this.ballPosition)},"velocity":${vector(this.ballVelocity)}},` +
      `"players":[${players}]}`
    );
  }

  /** Stop recording. Everything already recorded stays readable. */
  stop(): void {
    this.stopped = true;
  }

  /** The whole recording, header line first, with the trailing newline the format requires. */
  toText(): string {
    return `${this.lines.join('\n')}\n`;
  }

  toBlob(): Blob {
    return new Blob([this.toText()], { type: 'application/x-ndjson' });
  }

  /**
   * Hand the recording to the browser as a file.
   *
   * The awkward part of tracing from a web page: there is no file to write to, so the bytes
   * leave either as a download or by being read out of `toText` by whatever is driving the
   * page.
   */
  download(filename = 'web-run.gftrace'): void {
    const url = URL.createObjectURL(this.toBlob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    // Revoked on the next turn of the event loop; revoking immediately races the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
